import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import CreditCardIcon from "@mui/icons-material/CreditCard";

const TokenPurchaseModal = ({
    open,
    onClose,
    onConfirm,
    selectedPack,
    cardInfo,
    isProcessing,
    isSuccess,
    errorMessage,
    creditsGranted,
    invoiceUrl,
}) => {
    return (
        <Dialog open={open} onClose={isProcessing ? null : onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ textAlign: "center", fontWeight: 700 }}>
                {isSuccess ? "Purchase Successful!" : "Confirm Token Purchase"}
            </DialogTitle>

            <DialogContent>
                {isProcessing ? (
                    <Box sx={{ py: 4, textAlign: "center" }}>
                        <CircularProgress size={60} sx={{ mb: 3 }} />
                        <Typography variant="h6" fontWeight={600}>
                            Processing Payment...
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                            Charging your card on file securely.
                        </Typography>
                    </Box>
                ) : isSuccess ? (
                    <Box sx={{ py: 2, textAlign: "center" }}>
                        <CheckCircleIcon sx={{ fontSize: 80, color: "#10b981", mb: 2 }} />
                        <Typography variant="h5" fontWeight={700} gutterBottom>
                            {creditsGranted?.toLocaleString()} Credits Added!
                        </Typography>
                        <Typography color="text.secondary">
                            Your top-up credits have been granted to your store.
                        </Typography>
                        {invoiceUrl && (
                            <Button
                                variant="outlined"
                                color="primary"
                                href={invoiceUrl}
                                target="_blank"
                                sx={{ mt: 3, textTransform: "none", fontWeight: 600 }}
                            >
                                Download Receipt (PDF)
                            </Button>
                        )}
                    </Box>
                ) : errorMessage ? (
                    <Box sx={{ py: 2, textAlign: "center" }}>
                        <ErrorIcon sx={{ fontSize: 80, color: "#ef4444", mb: 2 }} />
                        <Typography variant="h6" fontWeight={700} color="error" gutterBottom>
                            Payment Failed
                        </Typography>
                        <Typography color="text.secondary">
                            {errorMessage}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            You are about to purchase the following top-up pack:
                        </Typography>

                        <Box
                            sx={{
                                p: 2,
                                backgroundColor: "#f8fafc",
                                borderRadius: "8px",
                                border: "1px solid #e2e8f0",
                                mb: 3,
                            }}
                        >
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                <Typography fontWeight={600}>{selectedPack?.plan_name}</Typography>
                                <Typography fontWeight={700}>${selectedPack?.amount}</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {selectedPack?.credits?.toLocaleString()} Top-up Credits
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                            Payment Method
                        </Typography>

                        {cardInfo ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, backgroundColor: "#f1f5f9", borderRadius: "6px" }}>
                                <CreditCardIcon sx={{ color: "#475569" }} />
                                <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                        {cardInfo.card_type} ending in {cardInfo.card_number?.slice(-4)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Card on file will be charged immediately
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="error">
                                No card details found. Please contact support.
                            </Typography>
                        )}

                        <Typography variant="caption" display="block" sx={{ mt: 3, color: "#64748b", textAlign: "center" }}>
                            By clicking "Confirm", you authorize Ask Budi to charge your card for the total amount shown above.
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                {!isProcessing && !isSuccess && (
                    <>
                        <Button onClick={onClose} disabled={isProcessing} sx={{ textTransform: "none" }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={onConfirm}
                            disabled={isProcessing || !cardInfo}
                            fullWidth
                            sx={{ py: 1.2, textTransform: "none", fontWeight: 700 }}
                        >
                            Confirm & Pay ${selectedPack?.amount}
                        </Button>
                    </>
                )}
                {isSuccess && (
                    <Button variant="contained" onClick={onClose} fullWidth sx={{ py: 1.2, textTransform: "none", fontWeight: 700 }}>
                        Done
                    </Button>
                )}
                {errorMessage && !isProcessing && (
                    <Button variant="outlined" onClick={onClose} fullWidth sx={{ py: 1.2, textTransform: "none", fontWeight: 700 }}>
                        Close
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default TokenPurchaseModal;
